import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { useLocalStorageState } from '@components/component-helpers';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState } from '@components/empty-state';
import { ExpandableSearch } from '@components/expandable-search';
import { Header } from '@components/header';
import { COLOR_TO_VARIANT, ICON_CHOOSER_ICONS } from '@components/icon-chooser/icon-chooser';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconOnboarding } from '@components/icons/onboarding';
import { IconSortAsc } from '@components/icons/sort-asc';
import { IconSortDesc } from '@components/icons/sort-desc';
import { IconWorkspace } from '@components/icons/workspace';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { toQueryString } from '@utils/requests';

import { useJourneys } from 'api/journeys/hooks';
import { useWorkspace } from 'api/workspaces/hooks';

import { JourneyListItem } from './journey-item';
import { useCreateJourneyModal } from './modals/create-journey';

import { useCurrentWorkspace } from '../../helpers';
import { PageNotFound } from '../page-not-found';

const orderingItems = [
  'title',
  '-title',
  '-date_created',
  'date_created',
  '-date_updated',
  'date_updated',
  '-num_travellers',
  'num_travellers',
  '-num_actions',
  'num_actions',
] as const;

export const JourneyList = () => {
  const [searchStr, setSearchStr] = useState('');
  const {
    workspaceId,
    title: workspaceTitle,
    color: workspaceColor,
    icon: workspaceIcon,
  } = useCurrentWorkspace();
  const { isError: isWorkspaceError, isLoading: isWorkspaceLoading } = useWorkspace(workspaceId);
  const WorkspaceIcon = ICON_CHOOSER_ICONS[workspaceIcon] || IconWorkspace;
  const showCreateJourneyModal = useCreateJourneyModal();

  const [ordering, setOrdering] = useLocalStorageState<(typeof orderingItems)[number]>({
    key: 'journey-list-order',
    defaultValue: '-date_created',
  });
  const queryString = useMemo(() => {
    return toQueryString({
      ...(searchStr && { search: searchStr }),
      workspace: workspaceId,
      ordering,
    });
  }, [searchStr, ordering, workspaceId]);

  const { data, isLoading } = useJourneys(queryString, isWorkspaceError || isWorkspaceLoading);
  const { t } = useScopedTranslate(locale);
  const isDataEmpty = !data || data.length === 0;

  if (isWorkspaceError) {
    return <PageNotFound message={t('notFoundMessage')} />;
  }

  if (isLoading) return <Spinner />;

  const isDescendingOrdering = ordering.startsWith('-');
  return (
    <>
      <Header
        actions={
          <Stack space={2} orientation="horizontal">
            <ExpandableSearch onChange={setSearchStr} dataTestid="search-journeys" />
            <Menu
              maxWidth="auto"
              width="auto"
              buttonProps={{
                variant: 'secondary',
                text: t(`sort_${ordering}`).split(' • ')[0],
                iconLeft: isDescendingOrdering ? <IconSortDesc /> : <IconSortAsc />,
              }}
              dataTestid="order-journeys-btn"
            >
              <Menu.Label label={t('sort')} />
              {orderingItems.map((it) => (
                <Menu.Item
                  key={it}
                  onSelect={() => setOrdering(it)}
                  isActive={ordering === it}
                  label={t(`sort_${it}`)}
                />
              ))}
            </Menu>
            <Button
              data-cy="new-journey-btn"
              text={t('addJourney')}
              iconLeft={<IconJourneyItem />}
              onClick={() => showCreateJourneyModal({ workspaceId })}
            />
          </Stack>
        }
        breadcrumbs={<Breadcrumb items={[{ label: workspaceTitle }, { label: t('journeys') }]} />}
        backAction={
          <IconContainer
            hasBorder
            icon={<WorkspaceIcon color={workspaceColor} />}
            variant={COLOR_TO_VARIANT[workspaceColor]}
          />
        }
      />
      <PageContent>
        {isDataEmpty ? (
          searchStr ? (
            <EmptySearchState skipMargin />
          ) : (
            <EmptyState
              skipMargin
              header={t('emptyStateHeader')}
              content={t('emptyStateContent')}
              icon={<IconOnboarding size={80} />}
            />
          )
        ) : (
          <JourneyItemContainer space={2}>
            {data.map((journey) => {
              return <JourneyListItem key={journey.shortId} journey={journey} />;
            })}
          </JourneyItemContainer>
        )}
      </PageContent>
    </>
  );
};

const JourneyItemContainer = styled(Stack)({
  flexGrow: 1,
  position: 'relative',
  display: 'flex',
  '@media(max-width: 768px)': {
    overflowY: 'visible',
  },
});

const IconContainer = styled(IconFancyContainer)({
  borderWidth: pxToRem(1),
});

const locale = {
  en: {
    journeys: 'Journeys',
    addJourney: 'Create journey',
    emptyStateContent: 'Use journeys to define and manage processes for your employees',
    emptyStateHeader: 'No active journeys',
    sort: 'Sort by',
    sort_title: 'Alphabetical ascending • A → Z',
    'sort_-title': 'Alphabetical descending • Z → A',
    'sort_-date_created': 'Created • Newest',
    sort_date_created: 'Created • Oldest',
    'sort_-date_updated': 'Last edit • New → Old',
    sort_date_updated: 'Last edit • Old → New',
    'sort_-num_travellers': 'Traveller count • High → Low',
    sort_num_travellers: 'Traveller count • Low → High',
    'sort_-num_actions': 'Action count • High → Low',
    sort_num_actions: 'Action count • Low → High',
    notFoundMessage: 'Are you sure the workspace exists and you have access to it?',
  },
};
